import { getConfig } from '@klpkonsern/appconfig';
import type {
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import i18n from 'i18next';
import { StackdriverLogger } from '@klpkonsern/stackdriver-logger';
import { AuthService } from '@klpkonsern/auth-core';
import { showToast } from '@klpkonsern/ui';
import type { IApiStore } from '../store';
import { pushError, setRemoteSessionExpired } from '../store';

// Global flag to prevent multiple simultaneous token refresh attempts
let isTokenRefreshInProgress = false;

export const baseQuery = fetchBaseQuery({
    baseUrl: getConfig('WEBAPP_API_URL'),
    mode: 'cors',
    prepareHeaders: async (headers) => {
        const user = await AuthService.getUser();
        if (user) {
            headers.set('Authorization', `Bearer ${user.access_token}`);
        }
        return headers;
    },
});

const baseQueryWithErrorHandling = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object & {
        _tokenRefreshAttempted?: boolean;
        silent?: boolean;
    } = {},
) => {
    const {
        customApi: { remoteSessionExpired },
    } = api.getState() as IApiStore;

    // Prevent API calls if remote session is already expired
    if (remoteSessionExpired) {
        console.warn('Preventing API request due to expired session:', args);
        return {
            error: { status: 401, data: 'Session expired, blocking API calls' },
        };
    }

    const start = Date.now();
    const stack = new Error().stack;

    // Ensure extraOptions is always an object
    const safeExtraOptions = extraOptions || {};

    const result = await baseQuery(args, api, safeExtraOptions);

    const duration = Date.now() - start;

    if (result?.error) {
        const err = result.error as FetchBaseQueryError;

        // Fetch errors are not handled by the API, so we handle them here
        if (err.status === 'FETCH_ERROR') {
            const errorMessage = `A network error occurred. This could be a CORS issue or a problem with the network connection. Check the browser's console for more details.`;

            if (!safeExtraOptions.silent) {
                api.dispatch(pushError(0, errorMessage));
            }

            const logContext = {
                endpoint: api.endpoint,
                type: api.type,
                url: typeof args === 'string' ? args : args.url,
                durationMs: duration,
                error: err.error,
                stack,
            };

            const structuredError = new Error(errorMessage, {
                cause: logContext,
            });
            StackdriverLogger.log(structuredError);

            return result;
        }

        const status =
            typeof err.status === 'number'
                ? err.status
                : err.status === 'PARSING_ERROR'
                  ? err.originalStatus
                  : undefined;
        const payload = err.data as { message?: string } | string | null;

        let backendMessage: string | null = null;
        if (typeof payload === 'string' && payload) {
            backendMessage = payload;
        } else if (
            payload &&
            typeof payload === 'object' &&
            'message' in payload &&
            typeof payload.message === 'string' &&
            payload.message
        ) {
            backendMessage = payload.message;
        }

        const msg = backendMessage ?? `Request failed (${status})`;

        // Handle 401 Unauthorized errors
        if (status === 401) {
            // If we already attempted token refresh, don't try again
            if (
                safeExtraOptions._tokenRefreshAttempted ||
                isTokenRefreshInProgress
            ) {
                // Use setTimeout to avoid React render cycle issues
                setTimeout(() => {
                    api.dispatch(setRemoteSessionExpired(true));
                }, 0);
                return { error: { status: 401, data: msg } };
            }

            const meta = (result.meta as FetchBaseQueryMeta) || {};
            const wwwAuth =
                meta.response?.headers.get('www-authenticate') ?? '';
            const body =
                typeof err.data === 'object' && err.data !== null
                    ? err.data
                    : {};
            const desc = (body as Record<string, unknown>)
                ?.error_description as string;

            if (
                wwwAuth.toLowerCase().includes('invalid_token') ||
                desc?.toLowerCase().includes('invalid_token')
            ) {
                const errorMessage =
                    'Missing or invalid scope on access token. Please configure your .env with the correct audience/scope.';

                if (!safeExtraOptions.silent) {
                    api.dispatch(pushError(401, errorMessage));
                }

                const logContext = { args, duration, stack, wwwAuth, desc };
                StackdriverLogger.log(
                    new Error(errorMessage, { cause: logContext }),
                );

                return { error: { status: 401, data: errorMessage } };
            }

            // Set flag to prevent multiple simultaneous refresh attempts
            isTokenRefreshInProgress = true;

            try {
                // Try to refresh token silently
                const renewedUser = await AuthService.refreshTokenSilently();

                if (renewedUser) {
                    // Reset flag on success
                    isTokenRefreshInProgress = false;

                    // Try to call the API one more time with flag to prevent endless retries
                    const retryResult = await baseQuery(args, api, {
                        ...safeExtraOptions,
                        _tokenRefreshAttempted: true,
                    });
                    if (retryResult?.error) {
                        // Use setTimeout to avoid React render cycle issues
                        setTimeout(() => {
                            api.dispatch(setRemoteSessionExpired(true));
                        }, 0);
                    }
                    return retryResult;
                } else {
                    isTokenRefreshInProgress = false;
                    // Use setTimeout to avoid React render cycle issues
                    setTimeout(() => {
                        api.dispatch(setRemoteSessionExpired(true));
                    }, 0);
                    return { error: { status: 401, data: msg } };
                }
            } catch (error) {
                isTokenRefreshInProgress = false;
                // Use setTimeout to avoid React render cycle issues
                setTimeout(() => {
                    api.dispatch(setRemoteSessionExpired(true));
                }, 0);
                return { error: { status: 401, data: msg } };
            }
        }

        // Handle other 4xx Client Errors (operational severity → toast)
        if (status && status >= 400 && status < 500) {
            if (!safeExtraOptions.silent) {
                showToast({
                    body: backendMessage
                        ? i18n.t('general.error.messageWithDetails', {
                              errorMessage: backendMessage,
                          })
                        : i18n.t('general.error.message'),
                    type: 'error',
                });
            }

            const logContext = {
                endpoint: api.endpoint,
                type: api.type,
                url: typeof args === 'string' ? args : args.url,
                method:
                    typeof args === 'object' && 'method' in args
                        ? args.method
                        : 'GET',
                params: (args as FetchArgs).params,
                body: (args as FetchArgs).body,
                durationMs: duration,
                statusCode: status,
                rawStatus: err.status,
                stack,
            };

            const structuredError = new Error(msg, {
                cause: logContext,
            });
            StackdriverLogger.log(structuredError);
        }

        // Handle 5xx Server Errors (system-level severity → Snackbar)
        else if (status && status >= 500) {
            const errorMessage =
                'A server error occurred. Please try again later.';

            if (!safeExtraOptions.silent) {
                api.dispatch(pushError(status, errorMessage));
            }

            const structuredError = new Error(errorMessage, {
                cause: {
                    endpoint: api.endpoint,
                    type: api.type,
                    url: typeof args === 'string' ? args : args.url,
                    durationMs: duration,
                    statusCode: status,
                    rawStatus: err.status,
                    errorData: err.data,
                    stack,
                },
            });

            StackdriverLogger.log(structuredError);
        }
        // Fallback for uncategorized errors: TIMEOUT_ERROR, CUSTOM_ERROR,
        // PARSING_ERROR with non-4xx originalStatus, undefined/unknown status
        else {
            if (!safeExtraOptions.silent) {
                showToast({
                    body: i18n.t('general.error.message'),
                    type: 'error',
                });
            }

            const structuredError = new Error(
                `Uncategorized API error (rawStatus=${String(err.status)})`,
                {
                    cause: {
                        endpoint: api.endpoint,
                        type: api.type,
                        url: typeof args === 'string' ? args : args.url,
                        durationMs: duration,
                        rawStatus: err.status,
                        errorData: err.data,
                        stack,
                    },
                },
            );

            StackdriverLogger.log(structuredError);
        }
    }

    return result;
};

export const baseApi = createApi({
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: [
        'Process',
        'ContractToRegistration',
        'ContractToRegistrationList',
        'ContractToRegistrationStatistics',
        'ProsjektStatistics',
        'Kommentarer',
        'KommentarerFremhevede',
        'Config',
        'ByggFavoritter',
        'ProsjektProsess',
        'Vurderingsskjema',
        'SuperOfficeProsjekt',
        'Leverandorvurdering',
        'Brukerregion',
        'Brukerroller',
        'Vedlegg',
        'VedleggList',
        'VedleggContent',
        'ProjectStepTypes',
        'ByggProsjektkontroller',
        'ByggList',
        'BransjeRisiko',
        'frontpageAggregate',
        'CurrentUser',
        'Kostnadskalkyle',
        'ProsjektAvfall',
        'ReportMappings',
        'ReportMappingReports',
        'ReportMappingKunder',
        'ReportMappingBygg',
        'ReportMappingTypes',
        'ReportMapping',
        'Risikovurdering',
        'LeverandorVurderingProcess',
        'LeverandorVurdering',
        'LeverandorVedlegg',
        'LeverandorCompanySearch',
        'UtestaaendeFordringer',
        'PortfolioList',
        'RegionList',
    ],

    endpoints: () => ({}),
});
